import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building, MapPin, Home, DollarSign, Users, FileText,
  CreditCard, Wrench, Loader2, AlertCircle, CheckCircle, Clock,
  Calendar, Phone, Mail, BadgeCheck, Ban
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { propertyService } from '../services/propertyService';
import { tenantService } from '../services/tenantService';
import { leaseService } from '../services/leaseService';
import maintenanceService from '../services/maintenanceService';
import ActivityFeed from './ActivityFeed';

const TABS = [
  { id: 'tenants',     label: 'Tenants',     icon: Users },
  { id: 'leases',      label: 'Leases',       icon: FileText },
  { id: 'payments',    label: 'Payments',     icon: CreditCard },
  { id: 'maintenance', label: 'Maintenance',  icon: Wrench },
];

const StatusBadge = ({ status, variant = 'default' }) => {
  const styles = {
    Active:            'bg-green-100 text-green-800 border-green-200',
    active:            'bg-green-100 text-green-800 border-green-200',
    Inactive:          'bg-gray-100 text-gray-600 border-gray-200',
    inactive:          'bg-gray-100 text-gray-600 border-gray-200',
    Expired:           'bg-red-100 text-red-800 border-red-200',
    expired:           'bg-red-100 text-red-800 border-red-200',
    'Pending Approval':'bg-yellow-100 text-yellow-800 border-yellow-200',
    Open:              'bg-red-100 text-red-800 border-red-200',
    'In Progress':     'bg-blue-100 text-blue-800 border-blue-200',
    Resolved:          'bg-green-100 text-green-800 border-green-200',
    Closed:            'bg-gray-100 text-gray-600 border-gray-200',
    Scheduled:         'bg-purple-100 text-purple-800 border-purple-200',
    Emergency:         'bg-red-100 text-red-800 border-red-200',
    High:              'bg-orange-100 text-orange-800 border-orange-200',
    Medium:            'bg-yellow-100 text-yellow-800 border-yellow-200',
    Low:               'bg-green-100 text-green-800 border-green-200',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
      {status}
    </span>
  );
};

const EmptyRow = ({ cols, message }) => (
  <tr>
    <td colSpan={cols} className="px-4 py-8 text-center text-sm text-gray-400">{message}</td>
  </tr>
);

const PropertyDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [property, setProperty]     = useState(null);
  const [tenants, setTenants]       = useState([]);
  const [leases, setLeases]         = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [activeTab, setActiveTab]   = useState('tenants');

  useEffect(() => {
    loadAll();
  }, [id]);

  const loadAll = async () => {
    try {
      setLoading(true);
      setError('');
      const [propData, allTenants, allLeases, allMaintenance] = await Promise.all([
        propertyService.getById(id),
        tenantService.getAllTenants(),
        leaseService.getAll(),
        maintenanceService.getAll(),
      ]);

      setProperty(propData);
      setTenants(allTenants.filter(t => {
        const pId = t.property?._id || t.property;
        return pId?.toString() === id;
      }));
      setLeases(allLeases.filter(l => {
        const pId = l.property?._id || l.property;
        return pId?.toString() === id;
      }));
      setMaintenance(allMaintenance.filter(m => {
        const pId = m.property?._id || m.property;
        return pId?.toString() === id;
      }));
    } catch (err) {
      console.error('Failed to load property details:', err);
      setError('Failed to load property details.');
    } finally {
      setLoading(false);
    }
  };

  // ── Derived stats ──────────────────────────────────────────────────────────
  const occupiedUnits   = tenants.filter(t => t.status === 'Active').length;
  const monthlyRevenue  = leases
    .filter(l => l.status === 'active' || l.status === 'Active')
    .reduce((sum, l) => sum + (l.monthlyRent || 0), 0);
  const openMaintenance = maintenance.filter(m => m.status === 'Open' || m.status === 'In Progress').length;

  const fmt = (n) => n?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0.00';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

  const getMaintStatusIcon = (status) => {
    switch (status) {
      case 'Open':        return <AlertCircle className="h-3.5 w-3.5 text-red-500" />;
      case 'In Progress': return <Clock className="h-3.5 w-3.5 text-blue-500" />;
      case 'Resolved':
      case 'Closed':      return <CheckCircle className="h-3.5 w-3.5 text-green-500" />;
      default:            return <Wrench className="h-3.5 w-3.5 text-gray-400" />;
    }
  };

  // ── Payments derived from leases ───────────────────────────────────────────
  // Each active lease = one upcoming monthly payment; expired = historical
  const upcomingPayments = leases
    .filter(l => l.status === 'active' || l.status === 'Active')
    .map(l => {
      const now = new Date();
      const dueDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      return {
        _id:        l._id,
        tenant:     l.tenant?.name || '—',
        unit:       l.unitNumber || '—',
        amount:     l.monthlyRent || 0,
        type:       'Monthly Rent',
        dueDate,
        status:     'Due',
      };
    });

  const historicalPayments = leases
    .filter(l => l.status !== 'active' && l.status !== 'Active')
    .map(l => {
      const start = l.leaseStart ? new Date(l.leaseStart) : null;
      const end   = l.leaseEnd   ? new Date(l.leaseEnd)   : null;
      const months = (start && end)
        ? Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24 * 30)))
        : 1;
      return {
        _id:        l._id,
        tenant:     l.tenant?.name || '—',
        unit:       l.unitNumber || '—',
        amount:     (l.monthlyRent || 0) * months,
        type:       `Lease Total (${months} mo)`,
        dueDate:    l.leaseEnd ? new Date(l.leaseEnd) : null,
        status:     'Completed',
      };
    });

  // ── Loading / error ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/properties')} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back to Properties
        </Button>
        <p className="text-sm text-red-600">{error || 'Property not found.'}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6 items-start">

    {/* ── Left column: main content ──────────────────────────────────────── */}
    <div className="flex-1 min-w-0 space-y-6 w-full">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-start gap-3 min-w-0">
        <Button variant="ghost" size="sm" onClick={() => navigate('/properties')} className="mt-0.5 gap-1 flex-shrink-0">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <Building className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight truncate">{property.name}</h2>
          </div>
          <div className="flex items-center gap-1.5 mt-1 text-sm text-muted-foreground min-w-0">
            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate">{property.address}</span>
          </div>
        </div>
      </div>

      {/* ── Stat Cards ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground font-medium">Total Units</span>
              <Home className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold">{property.units ?? '—'}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{occupiedUnits} occupied</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground font-medium">Tenants</span>
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold">{tenants.length}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{tenants.filter(t => t.status === 'Active').length} active</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground font-medium">Monthly Revenue</span>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold">${fmt(monthlyRevenue)}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{leases.filter(l => l.status === 'active' || l.status === 'Active').length} active leases</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground font-medium">Open Maintenance</span>
              <Wrench className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className={`text-2xl font-bold ${openMaintenance > 0 ? 'text-red-600' : ''}`}>{openMaintenance}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{maintenance.length} total</div>
          </CardContent>
        </Card>
      </div>

      {/* ── Tabs ───────────────────────────────────────────────────────────── */}
      <div className="border-b overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {TABS.map(({ id: tid, label, icon: Icon }) => (
            <button
              key={tid}
              onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tid
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
              <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                activeTab === tid ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-500'
              }`}>
                {tid === 'tenants'     ? tenants.length
                 : tid === 'leases'   ? leases.length
                 : tid === 'payments' ? upcomingPayments.length + historicalPayments.length
                 : maintenance.length}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab Content ────────────────────────────────────────────────────── */}

      {/* Tenants */}
      {activeTab === 'tenants' && (
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Unit</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Email</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Phone</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Move-in</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {tenants.length === 0
                ? <EmptyRow cols={6} message="No tenants associated with this property." />
                : tenants.map(t => (
                  <tr key={t._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{t.name}</td>
                    <td className="px-4 py-3 text-gray-600">{t.unitNumber || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">
                      <div className="flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5 text-gray-400" />
                        {t.email || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <div className="flex items-center gap-1.5">
                        <Phone className="h-3.5 w-3.5 text-gray-400" />
                        {t.phone || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{fmtDate(t.moveInDate)}</td>
                    <td className="px-4 py-3"><StatusBadge status={t.status || 'Active'} /></td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}

      {/* Leases */}
      {activeTab === 'leases' && (
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Tenant</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Unit</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Monthly Rent</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Deposit</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Start</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">End</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {leases.length === 0
                ? <EmptyRow cols={7} message="No leases associated with this property." />
                : leases.map(l => (
                  <tr key={l._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{l.tenant?.name || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{l.unitNumber || '—'}</td>
                    <td className="px-4 py-3 text-gray-900 font-medium">
                      <span className="flex items-center gap-0.5">
                        <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                        {fmt(l.monthlyRent)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {l.securityDeposit ? `$${fmt(l.securityDeposit)}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{fmtDate(l.leaseStart)}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{fmtDate(l.leaseEnd)}</td>
                    <td className="px-4 py-3"><StatusBadge status={l.status || 'active'} /></td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}

      {/* Payments */}
      {activeTab === 'payments' && (
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <Calendar className="h-4 w-4" /> Upcoming Payments
            </h3>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Tenant</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Unit</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Amount</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Due Date</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {upcomingPayments.length === 0
                    ? <EmptyRow cols={6} message="No upcoming payments. No active leases found." />
                    : upcomingPayments.map(p => (
                      <tr key={p._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{p.tenant}</td>
                        <td className="px-4 py-3 text-gray-600">{p.unit}</td>
                        <td className="px-4 py-3 text-gray-600">{p.type}</td>
                        <td className="px-4 py-3 font-medium text-gray-900">${fmt(p.amount)}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{fmtDate(p.dueDate)}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 border-yellow-200">
                            Due
                          </span>
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          </div>

          {historicalPayments.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" /> Historical Lease Payments
              </h3>
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Tenant</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Unit</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Total Amount</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">End Date</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {historicalPayments.map(p => (
                      <tr key={p._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{p.tenant}</td>
                        <td className="px-4 py-3 text-gray-600">{p.unit}</td>
                        <td className="px-4 py-3 text-gray-600">{p.type}</td>
                        <td className="px-4 py-3 font-medium text-gray-900">${fmt(p.amount)}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{fmtDate(p.dueDate)}</td>
                        <td className="px-4 py-3">
                          <StatusBadge status="Completed" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <p className="text-xs text-gray-400 mt-1">
            Payment amounts are derived from lease records. Full payment transaction history is not yet tracked.
          </p>
        </div>
      )}

      {/* Maintenance */}
      {activeTab === 'maintenance' && (
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Title</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Priority</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Category</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Tenant</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {maintenance.length === 0
                ? <EmptyRow cols={6} message="No maintenance requests for this property." />
                : maintenance.map(m => (
                  <tr
                    key={m._id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate('/maintenance')}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{m.title}</div>
                      <div className="text-xs text-gray-400 line-clamp-1 mt-0.5">{m.description}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {getMaintStatusIcon(m.status)}
                        <span className="text-gray-700">{m.status}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={m.priority} /></td>
                    <td className="px-4 py-3 text-gray-600">{m.category}</td>
                    <td className="px-4 py-3 text-gray-600">{m.tenant?.name || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(m.createdAt)}</td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}
    </div>{/* end left column */}

    {/* ── Right column: Activity Feed ────────────────────────────────────── */}
    <div className="w-full lg:w-80 lg:flex-shrink-0 border-t pt-4 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-6">
      <div className="lg:sticky lg:top-4">
        <ActivityFeed
          propertyId={id}
          title="Property Activity"
        />
      </div>
    </div>

    </div>
  );
};

export default PropertyDetail;
